var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/*
Reference: https://datatracker.ietf.org/doc/html/rfc3447
  rSAES-OAEP-Default-Identifier    RSAES-AlgorithmIdentifier ::= {
      algorithm   id-RSAES-OAEP,
      parameters  RSAES-OAEP-params : {
          hashAlgorithm       sha1,
          maskGenAlgorithm    mgf1SHA1,
          pSourceAlgorithm    pSpecifiedEmpty
      }
  }
*/
const RSA_SPEC = {
    name: "RSA-OAEP",
    modulusLength: 2048,
    publicExponent: new Uint8Array([1, 0, 1]),
    hash: "SHA-1",
};
const arrayBufferToBase64 = (buffer) => {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i += 1) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
};
const base64ToArrayBuffer = (base64) => {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i += 1) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
};
const exportRSAKey = (scheme, key) => __awaiter(void 0, void 0, void 0, function* () { return arrayBufferToBase64(yield window.crypto.subtle.exportKey(scheme, key)); });
export const generateKeyPair = () => __awaiter(void 0, void 0, void 0, function* () {
    const keyPair = (yield window.crypto.subtle.generateKey(RSA_SPEC, true, [
        "encrypt",
        "decrypt",
    ]));
    return {
        publicKey: yield exportRSAKey("spki", keyPair.publicKey),
        privateKey: yield exportRSAKey("pkcs8", keyPair.privateKey),
    };
});
export const generateSecretKey = () => __awaiter(void 0, void 0, void 0, function* () {
    const keyData = yield window.crypto.subtle.generateKey({
        name: "AES-CBC",
        length: 256,
    }, true, ["encrypt", "decrypt"]);
    const secretKey = arrayBufferToBase64(yield window.crypto.subtle.exportKey("raw", keyData));
    return {
        secretKey,
    };
});
export const generateSecureIV = () => {
    const iv = window.crypto.getRandomValues(new Uint8Array(16));
    return {
        secureIV: arrayBufferToBase64(iv),
    };
};
export const encryptKey = (options) => __awaiter(void 0, void 0, void 0, function* () {
    const key = yield window.crypto.subtle.importKey("spki", base64ToArrayBuffer(options.publicKey), { name: RSA_SPEC.name, hash: RSA_SPEC.hash }, true, ["encrypt"]);
    const res = yield window.crypto.subtle.encrypt({ name: RSA_SPEC.name }, key, base64ToArrayBuffer(btoa(options.key)));
    return {
        encryptedKey: arrayBufferToBase64(res),
    };
});
export const decryptKey = (options) => __awaiter(void 0, void 0, void 0, function* () {
    const key = yield window.crypto.subtle.importKey("pkcs8", base64ToArrayBuffer(options.privateKey), { name: RSA_SPEC.name, hash: RSA_SPEC.hash }, true, ["decrypt"]);
    const res = yield window.crypto.subtle.decrypt({ name: RSA_SPEC.name }, key, base64ToArrayBuffer(options.key));
    return {
        decryptedKey: atob(arrayBufferToBase64(res)),
    };
});
export const encryptMessage = (options) => __awaiter(void 0, void 0, void 0, function* () {
    const key = base64ToArrayBuffer(options.secretKey);
    const iv = base64ToArrayBuffer(options.secureIV);
    const aesKey = yield window.crypto.subtle.importKey("raw", key, "AES-CBC", true, [
        "encrypt",
    ]);
    const cipherBuffer = yield window.crypto.subtle.encrypt({
        name: "AES-CBC",
        iv,
    }, aesKey, new TextEncoder().encode(options.message));
    return {
        encryptedMessage: arrayBufferToBase64(cipherBuffer),
    };
});
export const decryptMessage = (options) => __awaiter(void 0, void 0, void 0, function* () {
    const key = base64ToArrayBuffer(options.secretKey);
    const iv = base64ToArrayBuffer(options.secureIV);
    const aesKey = yield window.crypto.subtle.importKey("raw", key, "AES-CBC", true, [
        "decrypt",
    ]);
    const msgBuffer = yield window.crypto.subtle.decrypt({
        name: "AES-CBC",
        iv,
    }, aesKey, base64ToArrayBuffer(options.message));
    return {
        decryptedMessage: new TextDecoder().decode(msgBuffer),
    };
});
export const generateSHA256 = (options) => __awaiter(void 0, void 0, void 0, function* () {
    const msgUint8 = new TextEncoder().encode(options.input);
    const hashBuffer = yield window.crypto.subtle.digest("SHA-256", msgUint8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    return {
        hash: hashHex,
    };
});
//# sourceMappingURL=e2ee.js.map